/**
 * helpGuideData.js â€” Step-by-step contextual help guides for each feature page.
 *
 * These guides appear as "How?" buttons on each feature page header.
 * The user is ALREADY on the feature page, so content should explain
 * how to use the feature right here â€” not how to navigate to it.
 *
 * Each guide is keyed by a numeric `id` (1â€“8 for forwarded/main features, 11â€“12 for manual entry).
 * A guide contains:
 *   title       â€“ Modal heading
 *   subtitle    â€“ Optional one-liner under the heading
 *   platforms[] â€“ Tabbed sections (e.g. per email client). Even single-platform
 *                 items use this array (tabs simply won't render if length === 1).
 *   finalStep   â€“ HTML string shown on the completion screen
 *   actionLabel â€“ CTA button text (used when setHeroContent is provided)
 *
 * Each platform has:
 *   name   â€“ Tab label (also used to auto-resolve an icon)
 *   steps[]  â€“ Array of { instruction, detail?, highlight? }
 *     instruction â€“ The main step text (HTML allowed)
 *     detail      â€“ (optional) Tip text shown below the instruction
 *     highlight   â€“ (optional) A value that should be rendered as a copyable chip
 *
 * All user-visible strings are looked up via the `t()` translation function.
 * Pass the result of `useTranslation().t` to `getHelpGuideData(t)` to get
 * a fully-translated guide map. The `highlight` values (email addresses,
 * phone numbers) and `navigateAction` routing keys are NOT translated.
 */

/**
 * Returns the full help guide data map with all strings translated using `t`.
 * @param {function} t - Translation function from useTranslation()
 */
export function getHelpGuideData(t) {
  return {
    /* ====================================================================
       1 â€” Forwarded Email Analysis  /  Manual Email Analysis
       ==================================================================== */
    1: {
      title: t("helpGuide.1.title"),
      subtitle: t("helpGuide.1.subtitle"),
      platforms: [
        {
          name: t("helpGuide.1.p0.name"),
          steps: [
            {
              instruction: t("helpGuide.1.p0.s0.instruction"),
              detail: t("helpGuide.1.p0.s0.detail"),
            },
            {
              instruction: t("helpGuide.1.p0.s1.instruction"),
              detail: t("helpGuide.1.p0.s1.detail"),
            },
            {
              instruction: t("helpGuide.1.p0.s2.instruction"),
              highlight: "security@kinprotect.com",
              detail: t("helpGuide.1.p0.s2.detail"),
            },
            {
              instruction: t("helpGuide.1.p0.s3.instruction"),
              detail: t("helpGuide.1.p0.s3.detail"),
            },
          ],
        },
        {
          name: t("helpGuide.1.p1.name"),
          steps: [
            {
              instruction: t("helpGuide.1.p1.s0.instruction"),
              detail: t("helpGuide.1.p1.s0.detail"),
            },
            {
              instruction: t("helpGuide.1.p1.s1.instruction"),
              detail: t("helpGuide.1.p1.s1.detail"),
            },
            {
              instruction: t("helpGuide.1.p1.s2.instruction"),
              highlight: "security@kinprotect.com",
              detail: t("helpGuide.1.p1.s2.detail"),
            },
            {
              instruction: t("helpGuide.1.p1.s3.instruction"),
              detail: t("helpGuide.1.p1.s3.detail"),
            },
          ],
        },
        {
          name: t("helpGuide.1.p2.name"),
          steps: [
            {
              instruction: t("helpGuide.1.p2.s0.instruction"),
              detail: t("helpGuide.1.p2.s0.detail"),
            },
            {
              instruction: t("helpGuide.1.p2.s1.instruction"),
              detail: t("helpGuide.1.p2.s1.detail"),
            },
            {
              instruction: t("helpGuide.1.p2.s2.instruction"),
              highlight: "security@kinprotect.com",
              detail: t("helpGuide.1.p2.s2.detail"),
            },
            {
              instruction: t("helpGuide.1.p2.s3.instruction"),
              detail: t("helpGuide.1.p2.s3.detail"),
            },
          ],
        },
        {
          name: t("helpGuide.1.p3.name"),
          steps: [
            {
              instruction: t("helpGuide.1.p3.s0.instruction"),
            },
            {
              instruction: t("helpGuide.1.p3.s1.instruction"),
            },
            {
              instruction: t("helpGuide.1.p3.s2.instruction"),
              highlight: "security@kinprotect.com",
              detail: t("helpGuide.1.p3.s2.detail"),
            },
            {
              instruction: t("helpGuide.1.p3.s3.instruction"),
            },
          ],
        },
      ],
      finalStep: t("helpGuide.1.finalStep"),
      actionLabel: t("helpGuide.1.actionLabel"),
    },

    /* ====================================================================
       2 â€” Forwarded Text/SMS Analysis  /  Manual Text Analysis
       ==================================================================== */
    2: {
      title: t("helpGuide.2.title"),
      subtitle: t("helpGuide.2.subtitle"),
      platforms: [
        {
          name: t("helpGuide.2.p0.name"),
          steps: [
            {
              instruction: t("helpGuide.2.p0.s0.instruction"),
              detail: t("helpGuide.2.p0.s0.detail"),
            },
            {
              instruction: t("helpGuide.2.p0.s1.instruction"),
              detail: t("helpGuide.2.p0.s1.detail"),
            },
            {
              instruction: t("helpGuide.2.p0.s2.instruction"),
            },
            {
              instruction: t("helpGuide.2.p0.s3.instruction"),
              highlight: "+1-888-899-5004",
              detail: t("helpGuide.2.p0.s3.detail"),
            },
            {
              instruction: t("helpGuide.2.p0.s4.instruction"),
              detail: t("helpGuide.2.p0.s4.detail"),
            },
          ],
        },
        {
          name: t("helpGuide.2.p1.name"),
          steps: [
            {
              instruction: t("helpGuide.2.p1.s0.instruction"),
              detail: t("helpGuide.2.p1.s0.detail"),
            },
            {
              instruction: t("helpGuide.2.p1.s1.instruction"),
              detail: t("helpGuide.2.p1.s1.detail"),
            },
            {
              instruction: t("helpGuide.2.p1.s2.instruction"),
              detail: t("helpGuide.2.p1.s2.detail"),
            },
            {
              instruction: t("helpGuide.2.p1.s3.instruction"),
              highlight: "+1-888-899-5004",
            },
            {
              instruction: t("helpGuide.2.p1.s4.instruction"),
              detail: t("helpGuide.2.p1.s4.detail"),
            },
          ],
        },
      ],
      finalStep: t("helpGuide.2.finalStep"),
      actionLabel: t("helpGuide.2.actionLabel"),
    },

    /* ====================================================================
       3 â€” Text Conversation Analysis
       ==================================================================== */
    3: {
      title: t("helpGuide.3.title"),
      subtitle: t("helpGuide.3.subtitle"),
      platforms: [
        {
          name: t("helpGuide.3.p0.name"),
          steps: [
            {
              instruction: t("helpGuide.3.p0.s0.instruction"),
              detail: t("helpGuide.3.p0.s0.detail"),
            },
            {
              instruction: t("helpGuide.3.p0.s1.instruction"),
              detail: t("helpGuide.3.p0.s1.detail"),
            },
            {
              instruction: t("helpGuide.3.p0.s2.instruction"),
              detail: t("helpGuide.3.p0.s2.detail"),
            },
          ],
        },
      ],
      finalStep: t("helpGuide.3.finalStep"),
      actionLabel: t("helpGuide.3.actionLabel"),
    },

    /* ====================================================================
       4 â€” URL / Social Media Post Safety Analysis
       ==================================================================== */
    4: {
      title: t("helpGuide.4.title"),
      subtitle: t("helpGuide.4.subtitle"),
      platforms: [
        {
          name: t("helpGuide.4.p0.name"),
          steps: [
            {
              instruction: t("helpGuide.4.p0.s0.instruction"),
              detail: t("helpGuide.4.p0.s0.detail"),
            },
            {
              instruction: t("helpGuide.4.p0.s1.instruction"),
              detail: t("helpGuide.4.p0.s1.detail"),
            },
            {
              instruction: t("helpGuide.4.p0.s2.instruction"),
            },
            {
              instruction: t("helpGuide.4.p0.s3.instruction"),
              detail: t("helpGuide.4.p0.s3.detail"),
            },
          ],
        },
        {
          name: t("helpGuide.4.p1.name"),
          steps: [
            {
              instruction: t("helpGuide.4.p1.s0.instruction"),
              detail: t("helpGuide.4.p1.s0.detail"),
            },
            {
              instruction: t("helpGuide.4.p1.s1.instruction"),
              detail: t("helpGuide.4.p1.s1.detail"),
            },
            {
              instruction: t("helpGuide.4.p1.s2.instruction"),
            },
            {
              instruction: t("helpGuide.4.p1.s3.instruction"),
              detail: t("helpGuide.4.p1.s3.detail"),
            },
          ],
          finalStep: t("helpGuide.4.p1.finalStep"),
          actionLabel: t("helpGuide.4.p1.actionLabel"),
          navigateAction: "Screenshot Analysis",
        },
        {
          name: t("helpGuide.4.p2.name"),
          steps: [
            {
              instruction: t("helpGuide.4.p2.s0.instruction"),
              detail: t("helpGuide.4.p2.s0.detail"),
            },
          ],
          finalStep: t("helpGuide.4.p2.finalStep"),
          actionLabel: t("helpGuide.4.p2.actionLabel"),
          navigateAction: "Screenshot Analysis",
        },
      ],
      finalStep: t("helpGuide.4.finalStep"),
      actionLabel: t("helpGuide.4.actionLabel"),
    },

    /* ====================================================================
       5 â€” Phone Number Lookup
       ==================================================================== */
    5: {
      title: t("helpGuide.5.title"),
      subtitle: t("helpGuide.5.subtitle"),
      platforms: [
        {
          name: t("helpGuide.5.p0.name"),
          steps: [
            {
              instruction: t("helpGuide.5.p0.s0.instruction"),
              detail: t("helpGuide.5.p0.s0.detail"),
            },
            {
              instruction: t("helpGuide.5.p0.s1.instruction"),
              detail: t("helpGuide.5.p0.s1.detail"),
            },
            {
              instruction: t("helpGuide.5.p0.s2.instruction"),
              detail: t("helpGuide.5.p0.s2.detail"),
            },
          ],
        },
      ],
      finalStep: t("helpGuide.5.finalStep"),
      actionLabel: t("helpGuide.5.actionLabel"),
    },

    /* ====================================================================
       6 â€” Screenshot / Image Analysis
       ==================================================================== */
    6: {
      title: t("helpGuide.6.title"),
      subtitle: t("helpGuide.6.subtitle"),
      platforms: [
        {
          name: t("helpGuide.6.p0.name"),
          steps: [
            {
              instruction: t("helpGuide.6.p0.s0.instruction"),
              detail: t("helpGuide.6.p0.s0.detail"),
            },
            {
              instruction: t("helpGuide.6.p0.s1.instruction"),
              detail: t("helpGuide.6.p0.s1.detail"),
            },
            {
              instruction: t("helpGuide.6.p0.s2.instruction"),
              detail: t("helpGuide.6.p0.s2.detail"),
            },
          ],
        },
        {
          name: t("helpGuide.6.p1.name"),
          steps: [
            {
              instruction: t("helpGuide.6.p1.s0.instruction"),
              detail: t("helpGuide.6.p1.s0.detail"),
            },
            {
              instruction: t("helpGuide.6.p1.s1.instruction"),
              detail: t("helpGuide.6.p1.s1.detail"),
            },
            {
              instruction: t("helpGuide.6.p1.s2.instruction"),
              detail: t("helpGuide.6.p1.s2.detail"),
            },
          ],
        },
        {
          name: t("helpGuide.6.p2.name"),
          steps: [
            {
              instruction: t("helpGuide.6.p2.s0.instruction"),
              detail: t("helpGuide.6.p2.s0.detail"),
            },
          ],
        },
      ],
      finalStep: t("helpGuide.6.finalStep"),
      actionLabel: t("helpGuide.6.actionLabel"),
    },

    /* ====================================================================
       7 â€” Social Media / Business Evaluation (Business/Person Check)
       ==================================================================== */
    7: {
      title: t("helpGuide.7.title"),
      subtitle: t("helpGuide.7.subtitle"),
      platforms: [
        {
          name: t("helpGuide.7.p0.name"),
          steps: [
            {
              instruction: t("helpGuide.7.p0.s0.instruction"),
              detail: t("helpGuide.7.p0.s0.detail"),
            },
            {
              instruction: t("helpGuide.7.p0.s1.instruction"),
              detail: t("helpGuide.7.p0.s1.detail"),
            },
            {
              instruction: t("helpGuide.7.p0.s2.instruction"),
              detail: t("helpGuide.7.p0.s2.detail"),
            },
          ],
        },
      ],
      finalStep: t("helpGuide.7.finalStep"),
      actionLabel: t("helpGuide.7.actionLabel"),
    },

    /* ====================================================================
       8 â€” Simulation Profile Settings
       ==================================================================== */
    8: {
      title: t("helpGuide.8.title"),
      subtitle: t("helpGuide.8.subtitle"),
      platforms: [
        {
          name: t("helpGuide.8.p0.name"),
          steps: [
            {
              instruction: t("helpGuide.8.p0.s0.instruction"),
              detail: t("helpGuide.8.p0.s0.detail"),
            },
            {
              instruction: t("helpGuide.8.p0.s1.instruction"),
              detail: t("helpGuide.8.p0.s1.detail"),
            },
            {
              instruction: t("helpGuide.8.p0.s2.instruction"),
              detail: t("helpGuide.8.p0.s2.detail"),
            },
          ],
        },
      ],
      finalStep: t("helpGuide.8.finalStep"),
      actionLabel: t("helpGuide.8.actionLabel"),
    },

    /* ====================================================================
       11 â€” Manual Email Analysis
       ==================================================================== */
    11: {
      title: t("helpGuide.11.title"),
      subtitle: t("helpGuide.11.subtitle"),
      platforms: [
        {
          name: t("helpGuide.11.p0.name"),
          steps: [
            {
              instruction: t("helpGuide.11.p0.s0.instruction"),
              detail: t("helpGuide.11.p0.s0.detail"),
            },
            {
              instruction: t("helpGuide.11.p0.s1.instruction"),
              detail: t("helpGuide.11.p0.s1.detail"),
            },
            {
              instruction: t("helpGuide.11.p0.s2.instruction"),
            },
            {
              instruction: t("helpGuide.11.p0.s3.instruction"),
              detail: t("helpGuide.11.p0.s3.detail"),
            },
            {
              instruction: t("helpGuide.11.p0.s4.instruction"),
              detail: t("helpGuide.11.p0.s4.detail"),
            },
            {
              instruction: t("helpGuide.11.p0.s5.instruction"),
              detail: t("helpGuide.11.p0.s5.detail"),
            },
          ],
        },
      ],
      finalStep: t("helpGuide.11.finalStep"),
      actionLabel: t("helpGuide.11.actionLabel"),
    },

    /* ====================================================================
       12 â€” Manual Text/SMS Analysis
       ==================================================================== */
    12: {
      title: t("helpGuide.12.title"),
      subtitle: t("helpGuide.12.subtitle"),
      platforms: [
        {
          name: t("helpGuide.12.p0.name"),
          steps: [
            {
              instruction: t("helpGuide.12.p0.s0.instruction"),
            },
            {
              instruction: t("helpGuide.12.p0.s1.instruction"),
              detail: t("helpGuide.12.p0.s1.detail"),
            },
            {
              instruction: t("helpGuide.12.p0.s2.instruction"),
              detail: t("helpGuide.12.p0.s2.detail"),
            },
            {
              instruction: t("helpGuide.12.p0.s3.instruction"),
              detail: t("helpGuide.12.p0.s3.detail"),
            },
            {
              instruction: t("helpGuide.12.p0.s4.instruction"),
              detail: t("helpGuide.12.p0.s4.detail"),
            },
          ],
        },
      ],
      finalStep: t("helpGuide.12.finalStep"),
      actionLabel: t("helpGuide.12.actionLabel"),
    },

    /* ====================================================================
       13 â€” Verify Caller Identity
       ==================================================================== */
    13: {
      title: t("helpGuide.13.title"),
      subtitle: t("helpGuide.13.subtitle"),
      platforms: [
        {
          name: t("helpGuide.13.p0.name"),
          steps: [
            {
              instruction: t("helpGuide.13.p0.s0.instruction"),
              detail: t("helpGuide.13.p0.s0.detail"),
            },
            {
              instruction: t("helpGuide.13.p0.s1.instruction"),
              detail: t("helpGuide.13.p0.s1.detail"),
            },
            {
              instruction: t("helpGuide.13.p0.s2.instruction"),
              detail: t("helpGuide.13.p0.s2.detail"),
            },
            {
              instruction: t("helpGuide.13.p0.s3.instruction"),
              detail: t("helpGuide.13.p0.s3.detail"),
            },
            {
              instruction: t("helpGuide.13.p0.s4.instruction"),
              detail: t("helpGuide.13.p0.s4.detail"),
            },
          ],
        },
      ],
      finalStep: t("helpGuide.13.finalStep"),
      actionLabel: t("helpGuide.13.actionLabel"),
    },

    /* ====================================================================
       14 â€” Choose a Simulation Profile
       ==================================================================== */
    14: {
      title: t("helpGuide.14.title"),
      subtitle: t("helpGuide.14.subtitle"),
      platforms: [
        {
          name: t("helpGuide.14.p0.name"),
          steps: [
            {
              instruction: t("helpGuide.14.p0.s0.instruction"),
              detail: t("helpGuide.14.p0.s0.detail"),
            },
            {
              instruction: t("helpGuide.14.p0.s1.instruction"),
              detail: t("helpGuide.14.p0.s1.detail"),
            },
            {
              instruction: t("helpGuide.14.p0.s2.instruction"),
              detail: t("helpGuide.14.p0.s2.detail"),
            },
            {
              instruction: t("helpGuide.14.p0.s3.instruction"),
              detail: t("helpGuide.14.p0.s3.detail"),
            },
          ],
        },
      ],
      finalStep: t("helpGuide.14.finalStep"),
      actionLabel: t("helpGuide.14.actionLabel"),
    },

    /* ====================================================================
       15 â€” User Management
       ==================================================================== */
    15: {
      title: t("helpGuide.15.title"),
      subtitle: t("helpGuide.15.subtitle"),
      platforms: [
        {
          name: t("helpGuide.15.p0.name"),
          steps: [
            {
              instruction: t("helpGuide.15.p0.s0.instruction"),
              detail: t("helpGuide.15.p0.s0.detail"),
            },
            {
              instruction: t("helpGuide.15.p0.s1.instruction"),
              detail: t("helpGuide.15.p0.s1.detail"),
            },
            {
              instruction: t("helpGuide.15.p0.s2.instruction"),
              detail: t("helpGuide.15.p0.s2.detail"),
            },
            {
              instruction: t("helpGuide.15.p0.s3.instruction"),
              detail: t("helpGuide.15.p0.s3.detail"),
            },
          ],
        },
      ],
      finalStep: t("helpGuide.15.finalStep"),
      actionLabel: t("helpGuide.15.actionLabel"),
    },
  };
}

export default getHelpGuideData;
